import NetworkBehavior from "../lib/NetworkBehavior";
import { Time } from "../lib/Time";
import { Physics } from "../math/Physics";
import Vector2 from "../math/Vector2";
import { clamp01 } from "../util";
import Color from "../util/Color";
import { Random } from "../util/Random";
import Bullet from "./Bullet";
import { FlakShard } from "./FlakShard";

enum State {
    MOVING,
    ACTIVE
}

export default class GravityWellBehavior extends NetworkBehavior {
    size = 512;
    state: State = State.MOVING;
    team: "red"|"blue"|undefined;
    dots: Vector2[] = [];
    targetPosition: Vector2 = Vector2.zero;
    born = Time.time;
    startPosition: Vector2|undefined;
    timer: number = 5;

    Die() {
        if (!this.engine.clientPlayer) return;

        this.engine.createExplosion({
            position: this.transform.position,
            size: 128,
            color: this.team === this.engine.clientPlayer?.team ? Color.fromHex("55ff55") : Color.fromHex("ff5555")
        });

        if (this.engine.clientPlayer.team !== this.team && Physics.CircleCircleCollision({
            position: this.transform.position,
            radius: 128
        }, {
            position: this.engine.clientPlayer.transform.position,
            radius: this.engine.clientPlayer.size
        }) !== false) {
            // this.engine.clientPlayer.hp -= 50;
            this.engine.clientPlayer.Damage(50);
        }

        if (this.engine.clientPlayer.team !== this.team) {
            const dist = Vector2.distance(this.transform.position, this.engine.clientPlayer.transform.position);

            if (Physics.CircleCircleCollision({
                position: this.transform.position,
                radius: this.size
            }, {
                position: this.engine.clientPlayer.transform.position,
                radius: this.engine.clientPlayer.size
            }) !== false) {
                const vec = this.transform.position.sub(this.engine.clientPlayer.transform.position).normalized;
                const power = 300 * (1 - dist/this.size);
                this.engine.clientPlayer.velocity = this.engine.clientPlayer.velocity.sub(vec.scale(power));
            }
        }

        
        
        this.engine.RemoveGameObject(this.gameObject);
    }

    MovingUpdate() {
        if (!this.startPosition) this.startPosition = this.transform.position;

        const timeAlive = Time.time - this.born;
        const t = clamp01(timeAlive);

        this.transform.position = Vector2.lerp(this.startPosition, this.targetPosition, t);

        if (t === 1) {
            this.state = State.ACTIVE;
            this.engine.createExplosion({
                position: this.transform.position,
                size: 128,
                color: this.team === this.engine.clientPlayer?.team ? Color.fromHex("55ff55") : Color.fromHex("ff5555")
            });

            if (this.engine.clientPlayer && this.engine.clientPlayer.team !== this.team && Physics.CircleCircleCollision({
                position: this.transform.position,
                radius: 128
            }, {
                position: this.engine.clientPlayer.transform.position,
                radius: this.engine.clientPlayer.size
            }) !== false) {
                this.engine.clientPlayer.Damage(50);
            }
        }
    }

    ActiveUpdate() {
        if (!this.engine.clientPlayer) return;

        this.timer -= Time.deltaTime;

        if (this.timer <= 0) {
            this.Die();
            return;
        }

        for (const cannonBullet of this.engine.GetGameObjectsBehaviors<Bullet>(Bullet).filter(
            v => v.team === this.team
        )) {
            if (Physics.CircleCircleCollision({
                position: this.transform.position,
                radius: this.size
            }, {
                position: cannonBullet.transform.position,
                radius: 4
            }) !== false) {
                const vec = this.transform.position.sub(cannonBullet.transform.position).normalized;
                cannonBullet.velocity = cannonBullet.velocity.add(vec.scale(150 * Time.deltaTime));
            }
        }

        for (const flak of this.engine.GetGameObjectsBehaviors<FlakShard>(FlakShard).filter(
            v => v.team === this.team
        )) {
            if (Physics.CircleCircleCollision({
                position: this.transform.position,
                radius: this.size
            }, {
                position: flak.transform.position,
                radius: 4
            }) !== false) {
                const vec = this.transform.position.sub(flak.transform.position).normalized;
                flak.velocity = flak.velocity.add(vec.scale(150 * Time.deltaTime));
            }
        }

        if (this.engine.clientPlayer.team === this.team) return;

        if (Physics.CircleCircleCollision({
            position: this.transform.position,
            radius: this.size
        }, {
            position: this.engine.clientPlayer.transform.position,
            radius: this.engine.clientPlayer.size
        }) !== false) {
            const vec = this.transform.position.sub(this.engine.clientPlayer.transform.position).normalized;
            this.engine.clientPlayer.velocity = this.engine.clientPlayer.velocity.add(vec.scale(150 * Time.deltaTime));
        }
    }

    Update(): void {

        switch (this.state) {
            case State.MOVING:
                this.MovingUpdate();
                break;
            case State.ACTIVE:
                this.ActiveUpdate();
                break;
        
            default:
                break;
        }
    }

    LateUpdate(): void {
        if (this.dots.length < 200) {
            const rt = Random.float(0, 2 * Math.PI);
            const vec = Vector2.fromRadians(rt).scale(Random.int(16, this.size));
            this.dots.push(this.transform.position.add(vec));
        }

        for (let i = this.dots.length - 1; i >= 0; i--) {
            const dot = this.dots[i];
            const travelVec = this.transform.position.sub(dot);
            this.dots[i] = dot.add(travelVec.scale(Time.deltaTime));

            if (Vector2.distance(this.dots[i], this.transform.position) <= 4) {
                this.dots.splice(i, 1);
            }
        }
    }

    Draw(): void {
        if (this.state === State.ACTIVE) {
            this.ctx.strokeStyle = this.engine.clientPlayer?.team === this.team ? '#55ff55' : '#ff5555';
            this.ctx.lineWidth = 1;
            this.engine.StrokeCircle(this.transform.position, this.size);

            this.engine.StrokeCircle(this.transform.position, this.size * (this.timer/5));

            this.ctx.fillStyle = '#ffffff55';
            for (const dot of this.dots) {
                this.engine.FillCircle(dot, 1);
            }
        } else {
            this.ctx.fillStyle = 'white';
            this.engine.FillCircle(this.transform.position, 2);
        }
    }

    DrawGizmos(): void {
        this.engine.drawTextsWorldPosition(
            [
                `${this.team}`
            ],
            this.transform.position,
            {
                color: this.engine.viewport.fColor
            }
        )
    }
}